/**
 * SupportSessionList
 * ------------------
 * Displays the live list of pending support sessions received via the
 * supportChannel WebSocket. Each row shows user info and a "Join" button.
 */

import React from 'react';
import {
  List,
  ListItem,
  ListItemText,
  Button,
  Typography,
  Paper,
  Box,
  Divider,
} from '@mui/material';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';

export interface SupportRequest {
  sessionId: string;
  userId: number;
  question: string;
  timestamp: number;
}

interface Props {
  sessions: SupportRequest[];
  activeSessionId: string | null;
  onJoin: (sessionId: string) => void;
}

const SupportSessionList: React.FC<Props> = ({ sessions, activeSessionId, onJoin }) => {
  return (
    <Paper
      elevation={2}
      sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
    >
      <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SupportAgentIcon color="primary" />
          <Typography variant="h6" fontWeight={600}>
            Pending Requests
          </Typography>
          {sessions.length > 0 && (
            <Box
              sx={{
                ml: 'auto',
                bgcolor: 'error.main',
                color: 'white',
                borderRadius: '50%',
                width: 24,
                height: 24,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 12,
                fontWeight: 700,
              }}
            >
              {sessions.length}
            </Box>
          )}
        </Box>
      </Box>

      {sessions.length === 0 ? (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'text.secondary',
            p: 3,
            textAlign: 'center',
          }}
        >
          <SupportAgentIcon sx={{ fontSize: 48, mb: 1, opacity: 0.3 }} />
          <Typography variant="body2">No pending support requests</Typography>
        </Box>
      ) : (
        <List sx={{ flex: 1, overflowY: 'auto', p: 0 }}>
          {sessions.map((session, idx) => (
            <React.Fragment key={session.sessionId}>
              {idx > 0 && <Divider />}
              <ListItem
                sx={{
                  px: 2,
                  py: 1.5,
                  bgcolor: activeSessionId === session.sessionId ? 'action.selected' : 'inherit',
                  '&:hover': { bgcolor: 'action.hover' },
                }}
                secondaryAction={
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => onJoin(session.sessionId)}
                    disabled={activeSessionId === session.sessionId}
                    sx={{ minWidth: 72 }}
                  >
                    {activeSessionId === session.sessionId ? 'Active' : 'Join'}
                  </Button>
                }
              >
                <ListItemText
                  primary={
                    <Typography variant="body2" fontWeight={600}>
                      UserId: {session.userId}
                    </Typography>
                  }
                  secondary={
                    <>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          maxWidth: 160,
                          display: 'block',
                        }}
                      >
                        {session.question}
                      </Typography>
                      <Typography variant="caption" color="text.disabled">
                        {new Date(session.timestamp).toLocaleTimeString()}
                      </Typography>
                    </>
                  }
                />
              </ListItem>
            </React.Fragment>
          ))}
        </List>
      )}
    </Paper>
  );
};

export default SupportSessionList;
